import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type {
  CampaignReportRow,
  ReportCampaignListResponse,
  ReportCampaignPageSize,
} from "@multi-chat/shared";
import { REPORT_CAMPAIGN_PAGE_SIZES } from "@multi-chat/shared";
import { ReportResultState } from "./ReportResultState.js";
import type { ReportEmptyKind } from "./report-empty-kind.js";
import { LtrValue } from "../../components/common/LtrValue.js";
import { cn } from "../../lib/utils.js";
import { CampaignStatusBadge } from "../campaigns/CampaignStatusBadge.js";
import {
  campaignReportSenderPrimary,
  campaignReportSenderSecondary,
  formatCampaignReportRate,
  showingCampaignRangeBounds,
} from "./campaign-report-presentation.js";
import type { CampaignReportColumnId } from "./report-column-defs.js";
import { reportColumnHeaderDragHandlers } from "./report-column-header-drag.js";
import { ReportColumnResizeHandle } from "./ReportColumnResizeHandle.js";
import { ReportPaginationBar } from "./ReportPaginationBar.js";
import {
  contactsTableHeaderLabelClass,
  reportColumnStyle,
  reportTableBodyCellClass,
  reportTableBodyRowClass,
  reportTableClass,
  reportTableHeaderCellClass,
  reportTableHeadRowClass,
  reportTableScrollClass,
  reportTableWorkspaceClass,
} from "./report-table-presentation.js";
import { formatReportDateTime } from "./outgoing-report-presentation.js";

const NUMERIC_COLUMNS = new Set<CampaignReportColumnId>([
  "recipients",
  "processing",
  "sent",
  "delivered",
  "read",
  "failed",
  "excluded",
  "deliveryRate",
  "readRate",
]);

function campaignColumnLabel(
  t: (key: string) => string,
  id: CampaignReportColumnId,
): string {
  switch (id) {
    case "date":
      return t("reports.campaigns.columns.activityAt");
    case "campaign":
    case "status":
    case "sender":
    case "template":
    case "recipients":
    case "processing":
    case "sent":
    case "delivered":
    case "read":
    case "failed":
    case "excluded":
    case "deliveryRate":
    case "readRate":
      return t(`reports.campaigns.columns.${id}`);
    default: {
      const _exhaustive: never = id;
      return _exhaustive;
    }
  }
}

export function CampaignReportTable({
  list,
  loading,
  page,
  pageSize,
  emptyKind,
  visibleColumns,
  widths,
  onResizeColumn,
  onReorderColumn,
  onPageChange,
  onPageSizeChange,
  onResetFilters,
}: {
  list: ReportCampaignListResponse | null;
  loading: boolean;
  page: number;
  pageSize: number;
  emptyKind: ReportEmptyKind;
  visibleColumns: readonly CampaignReportColumnId[];
  widths: Readonly<Record<CampaignReportColumnId, number>>;
  onResizeColumn: (id: CampaignReportColumnId, width: number) => void;
  onReorderColumn: (fromId: CampaignReportColumnId, toId: CampaignReportColumnId) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: ReportCampaignPageSize) => void;
  onResetFilters: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const items = list?.items ?? [];
  const total = list?.totalCount ?? 0;
  const totalPages = list?.totalPages ?? 0;
  const { start, end } = showingCampaignRangeBounds(page, pageSize, total);
  const colCount = Math.max(visibleColumns.length, 1);
  const dragFromRef = useRef<CampaignReportColumnId | null>(null);
  const resizeRef = useRef<{
    id: CampaignReportColumnId;
    startX: number;
    startWidth: number;
    rtl: boolean;
  } | null>(null);

  useEffect(() => {
    const onMove = (event: PointerEvent) => {
      const active = resizeRef.current;
      if (active == null) return;
      const delta = active.rtl ? active.startX - event.clientX : event.clientX - active.startX;
      onResizeColumn(active.id, active.startWidth + delta);
    };
    const onUp = () => {
      resizeRef.current = null;
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [onResizeColumn]);

  return (
    <div className={reportTableWorkspaceClass} data-testid="reports-campaigns-table">
      <div className={reportTableScrollClass}>
        <table className={reportTableClass} style={{ tableLayout: "fixed", width: "max-content", minWidth: "100%" }}>
          <thead>
            <tr className={reportTableHeadRowClass}>
              {visibleColumns.map((columnId) => {
                const label = campaignColumnLabel(t, columnId);
                return (
                  <th
                    key={columnId}
                    className={cn(
                      reportTableHeaderCellClass,
                      NUMERIC_COLUMNS.has(columnId) && "text-end",
                    )}
                    style={reportColumnStyle(widths[columnId])}
                    {...reportColumnHeaderDragHandlers(columnId, dragFromRef, onReorderColumn)}
                    data-testid={`reports-campaigns-column-${columnId}`}
                  >
                    <span className={contactsTableHeaderLabelClass}>{label}</span>
                    <ReportColumnResizeHandle
                      label={t("reports.chrome.resizeColumn", { column: label })}
                      onResizeStart={(clientX) => {
                        const rtl =
                          typeof document !== "undefined" &&
                          getComputedStyle(document.documentElement).direction === "rtl";
                        resizeRef.current = {
                          id: columnId,
                          startX: clientX,
                          startWidth: widths[columnId],
                          rtl,
                        };
                      }}
                    />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 ? (
              Array.from({ length: 6 }, (_, index) => (
                <tr key={index} className={reportTableBodyRowClass}>
                  <td colSpan={colCount} className={reportTableBodyCellClass}>
                    <div className="h-4 w-full animate-pulse rounded bg-surface-muted motion-reduce:animate-none" />
                  </td>
                </tr>
              ))
            ) : items.length === 0 ? (
              <tr className={reportTableBodyRowClass}>
                <td colSpan={colCount} className={reportTableBodyCellClass}>
                  <ReportResultState
                    kind={emptyKind}
                    i18nPrefix="reports.campaigns"
                    onResetFilters={onResetFilters}
                    testId="reports-campaigns-empty"
                  />
                </td>
              </tr>
            ) : (
              items.map((row) => (
                <CampaignReportRowView
                  key={row.campaignId}
                  row={row}
                  locale={i18n.language}
                  visibleColumns={visibleColumns}
                  widths={widths}
                  onOpen={() => navigate(row.campaignPath)}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      <ReportPaginationBar
        page={page}
        pageSize={pageSize}
        totalCount={total}
        totalPages={Math.max(totalPages, 1)}
        pageSizes={REPORT_CAMPAIGN_PAGE_SIZES}
        onPageChange={onPageChange}
        onPageSizeChange={(size) => onPageSizeChange(size as ReportCampaignPageSize)}
        showingLabel={t("reports.campaigns.showingRange")}
        pageOfLabel={t("reports.campaigns.pageOf")}
        prevLabel={t("reports.campaigns.prev")}
        nextLabel={t("reports.campaigns.next")}
        pageSizeLabel={t("reports.campaigns.pageSize")}
        testIdPrefix="reports-campaigns"
        rangeStart={start}
        rangeEnd={end}
      />
    </div>
  );
}

function CampaignReportRowView({
  row,
  locale,
  visibleColumns,
  widths,
  onOpen,
}: {
  row: CampaignReportRow;
  locale: string;
  visibleColumns: readonly CampaignReportColumnId[];
  widths: Readonly<Record<CampaignReportColumnId, number>>;
  onOpen: () => void;
}): React.JSX.Element {
  const { i18n } = useTranslation();
  const secondary = campaignReportSenderSecondary(row);

  const renderCell = (columnId: CampaignReportColumnId): React.ReactNode => {
    switch (columnId) {
      case "campaign":
        return <span className="font-medium text-foreground">{row.name}</span>;
      case "status":
        return <CampaignStatusBadge status={row.status} />;
      case "date":
        return (
          <LtrValue className="tabular-nums text-[0.75rem]">
            {formatReportDateTime(row.activityAt, i18n.language)}
          </LtrValue>
        );
      case "sender":
        return (
          <div className="grid gap-0.5">
            <span className="truncate">{campaignReportSenderPrimary(row)}</span>
            {secondary ? (
              <LtrValue className="truncate text-[0.75rem] text-muted">{secondary}</LtrValue>
            ) : null}
          </div>
        );
      case "template":
        return <span className="truncate">{row.templateName ?? "—"}</span>;
      case "recipients":
        return row.total;
      case "processing":
        return row.processing;
      case "sent":
        return row.sent;
      case "delivered":
        return row.delivered;
      case "read":
        return row.read;
      case "failed":
        return row.failed;
      case "excluded":
        return row.excluded;
      case "deliveryRate":
        return formatCampaignReportRate(row.deliveryRate, locale);
      case "readRate":
        return formatCampaignReportRate(row.readRate, locale);
      default: {
        const _exhaustive: never = columnId;
        return _exhaustive;
      }
    }
  };

  return (
    <tr
      className={reportTableBodyRowClass}
      data-clickable="true"
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen();
        }
      }}
      tabIndex={0}
      data-testid={`reports-campaigns-row-${row.campaignId}`}
    >
      {visibleColumns.map((columnId) => (
        <td
          key={columnId}
          className={cn(
            reportTableBodyCellClass,
            NUMERIC_COLUMNS.has(columnId) && "text-end tabular-nums",
          )}
          style={reportColumnStyle(widths[columnId])}
        >
          {renderCell(columnId)}
        </td>
      ))}
    </tr>
  );
}
